import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ListenerRole } from '../../../../hooks/usePeers';
import { useUser, useUpdateUser } from '../../../../hooks/useUsers';
import { SystemMessage, useSystemMessages } from '../../../../hooks/useComms';
import Modal from '../../../../components/common/modal';
import { toast } from 'react-toastify';

interface RejectionModalData {
  templateId: number | null;
  message: string;
  sendMessage: boolean;
}

interface DisplayNameReviewProps {
  peerData: ListenerRole;
  refetchPeerData: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const DisplayNameReview: React.FC<DisplayNameReviewProps> = ({ peerData, refetchPeerData, onDirtyChange }) => {
  // Fetch user directly from /api/v2/users/{id} to get proposed_display_name,
  // which is not reliably present on the listener profile endpoint response.
  const { data: userData, refetch: refetchUser } = useUser(peerData.user?.id);

  const [currentDisplayName, setCurrentDisplayName] = useState('');
  const [proposedDisplayName, setProposedDisplayName] = useState('');
  const [proposedStatus, setProposedStatus] = useState<string>('pending');
  // Track whether we've done the initial population from server data
  const initializedRef = useRef(false);
  const savedCurrentDisplayNameRef = useRef('');
  const savedProposedDisplayNameRef = useRef('');
  const savedProposedStatusRef = useRef('pending');

  useEffect(() => {
    if (!userData || initializedRef.current) return;
    initializedRef.current = true;

    const current = userData.display_name || '';
    let proposed: string;
    let status: string;

    if (userData.display_name_rejected) {
      proposed = userData.proposed_display_name || '';
      status = 'rejected';
    } else if (userData.proposed_display_name) {
      proposed = userData.proposed_display_name;
      status = 'pending';
    } else {
      proposed = '';
      status = 'pending';
    }

    setCurrentDisplayName(current);
    setProposedDisplayName(proposed);
    setProposedStatus(status);

    savedCurrentDisplayNameRef.current = current;
    savedProposedDisplayNameRef.current = proposed;
    savedProposedStatusRef.current = status;
  }, [userData]);

  useEffect(() => {
    if (!initializedRef.current) return;
    const isDirty =
      currentDisplayName !== savedCurrentDisplayNameRef.current ||
      proposedDisplayName !== savedProposedDisplayNameRef.current ||
      proposedStatus !== savedProposedStatusRef.current;
    onDirtyChange?.(isDirty);
  }, [currentDisplayName, proposedDisplayName, proposedStatus, onDirtyChange]);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionModalData, setRejectionModalData] = useState<RejectionModalData>({
    templateId: null,
    message: '',
    sendMessage: true,
  });

  const { updateUser: updateUserCurrent } = useUpdateUser();
  const { updateUser: updateUserProposed } = useUpdateUser();
  const { data: systemMessages } = useSystemMessages();

  const displayNameRejectedMessages = useMemo(
    () => systemMessages?.filter((item: SystemMessage) => item.group?.key === 'display_name_rejected') ?? [],
    [systemMessages],
  );

  const userId = peerData.user?.id;

  const handleSaveCurrent = () => {
    updateUserCurrent.mutate(
      { userId, data: { display_name: currentDisplayName } },
      {
        onSuccess: () => {
          toast.success('Display name updated successfully');
          savedCurrentDisplayNameRef.current = currentDisplayName;
          onDirtyChange?.(false);
          refetchUser();
          refetchPeerData();
        },
        onError: () => toast.error('Failed to update display name'),
      },
    );
  };

  const proposedTrimmed = proposedDisplayName.trim();
  const canSaveProposed = proposedTrimmed.length > 0;

  const handleSaveProposed = () => {
    if (!proposedTrimmed) return;
    if (proposedStatus === 'rejected') {
      const firstMsg = displayNameRejectedMessages[0];
      setRejectionModalData({
        templateId: firstMsg?.id ?? null,
        message: firstMsg?.message ?? '',
        sendMessage: true,
      });
      setShowRejectModal(true);
      return;
    }

    submitProposedUpdate();
  };

  const submitProposedUpdate = (overridePayload?: Record<string, any>) => {
    let payload: Record<string, any>;

    if (proposedStatus === 'approved') {
      payload = { display_name: proposedDisplayName, proposed_display_name: null, display_name_rejected: false };
    } else if (proposedStatus === 'rejected') {
      payload = overridePayload ?? { display_name_rejected: true };
    } else {
      payload = { proposed_display_name: proposedDisplayName, display_name_rejected: false };
    }

    updateUserProposed.mutate(
      { userId, data: payload },
      {
        onSuccess: () => {
          const statusLabels: Record<string, string> = { approved: 'approved', rejected: 'rejected', pending: 'saved' };
          const statusLabel = statusLabels[proposedStatus] || 'saved';
          toast.success(`Display name ${statusLabel} successfully`);
          savedProposedDisplayNameRef.current = proposedDisplayName;
          savedProposedStatusRef.current = proposedStatus;
          onDirtyChange?.(false);
          setProposedDisplayName('');
          setProposedStatus('pending');
          initializedRef.current = false;
          setShowRejectModal(false);
          refetchUser();
          refetchPeerData();
        },
        onError: () => toast.error('Failed to update proposed display name'),
      },
    );
  };

  const handleRejectConfirm = () => {
    const payload: Record<string, any> = {
      display_name_rejected: true,
      ...(rejectionModalData.sendMessage && rejectionModalData.templateId
        ? {
            system_message_id: rejectionModalData.templateId,
            message: rejectionModalData.message,
          }
        : {}),
    };
    submitProposedUpdate(payload);
  };

  return (
    <>
      <Modal
        isModel={showRejectModal}
        InnerComponent={
          <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <h3 className="mb-4 text-lg font-bold text-gray-900">Reject Display Name</h3>

              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Rejection Message Template</label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={rejectionModalData.templateId ?? 'none'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'none') {
                      setRejectionModalData((prev) => ({ ...prev, templateId: null, message: '', sendMessage: false }));
                    } else {
                      const id = Number(val);
                      const msg = displayNameRejectedMessages.find((m: SystemMessage) => m.id === id);
                      setRejectionModalData((prev) => ({
                        ...prev,
                        templateId: id,
                        message: msg?.message ?? '',
                        sendMessage: true,
                      }));
                    }
                  }}
                >
                  <option value="none">Do NOT send message</option>
                  {displayNameRejectedMessages.map((m: SystemMessage) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {rejectionModalData.sendMessage && (
                <div className="mb-6">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    className="h-32 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={rejectionModalData.message}
                    onChange={(e) => setRejectionModalData((prev) => ({ ...prev, message: e.target.value }))}
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowRejectModal(false)}
                >
                  Cancel
                </button>
                <button
                  disabled={updateUserProposed.isLoading}
                  className="bg-red rounded px-4 py-2 text-sm font-semibold text-white opacity-100 hover:opacity-90 disabled:opacity-50"
                  onClick={handleRejectConfirm}
                >
                  {updateUserProposed.isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        }
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <p className="mb-3 text-sm font-medium text-gray-700">Current</p>
          <input
            type="text"
            value={currentDisplayName}
            onChange={(e) => setCurrentDisplayName(e.target.value)}
            className="mb-4 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="No current display name"
          />
          <button
            onClick={handleSaveCurrent}
            disabled={updateUserCurrent.isLoading}
            className="rounded bg-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            {updateUserCurrent.isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-gray-700">Proposed</p>
          <input
            type="text"
            value={proposedDisplayName}
            onChange={(e) => setProposedDisplayName(e.target.value)}
            className="mb-4 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="No proposed display name"
          />

          <div className="mb-3">
            <select
              value={proposedStatus}
              onChange={(e) => setProposedStatus(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="pending">Needs Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <button
            onClick={handleSaveProposed}
            disabled={updateUserProposed.isLoading || !canSaveProposed}
            className="rounded bg-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            {updateUserProposed.isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

export default DisplayNameReview;
